import { lazy } from 'react';
import { Navigate } from 'react-router-dom';

import { GRADING_ADMIN } from 'config';

// project imports
import MainLayout from 'layout/MainLayout';
import Loadable from 'ui-component/Loadable';
import { Typography } from '@mui/material';

// dashboard routing
const DashboardDefault = Loadable(lazy(() => import('views/dashboard/Default')));

// users routing
const TeachersDefault = Loadable(lazy(() => import('views/users/teachers/TeachersDefault')));
const ClassesDefault = Loadable(lazy(() => import('views/users/classes/ClassesDefault')));
const StudentsDefault = Loadable(lazy(() => import('views/users/students/StudentsDefault')));

// exams routing
const QuestionsDefault = Loadable(lazy(() => import('views/exams/questions/QuestionsDefault')));
const QuestionSheetsDefault = Loadable(lazy(() => import('views/exams/questionsheets/QuestionSheetsDefault')));
const TestsDefault = Loadable(lazy(() => import('views/exams/tests/TestsDefault')));
const EvaluationDefault = Loadable(lazy(() => import('views/exams/evaluation/EvaluationDefault')));

const GradingOverview = Loadable(lazy(() => import('views/grading/overview/GradingOverview')));
const GradingExam = Loadable(lazy(() => import('views/grading/exam/GradingExam')));
const GradingSheet = Loadable(lazy(() => import('views/grading/sheet/GradingSheet')));
const GradingTrust = Loadable(lazy(() => import('views/grading/trust/GradingTrust')));
const GradingReview = Loadable(lazy(() => import('views/grading/review/GradingReview')));
const ReviewCardScreen = Loadable(lazy(() => import('views/grading/review/ReviewCardScreen')));
const NearSilentScreen = Loadable(lazy(() => import('views/grading/review/NearSilentScreen')));
const StimulusReview = Loadable(lazy(() => import('views/grading/stimuli/StimulusReview')));
const GradingSettings = Loadable(lazy(() => import('views/grading/settings/GradingSettings')));

// administrator routing
const AdminsDefault = Loadable(lazy(() => import('views/users/teachers/TeachersDefault')));
const AcademiesDefault = Loadable(lazy(() => import('views/administrator/academies/AcademiesDefault')));
const StudentsBulkDefault = Loadable(lazy(() => import('views/administrator/students-bulk/StudentsBulkDefault')));
// const QuestionsBulkDefault = Loadable(lazy(() => import('views/administrator/questions-bulk/QuestionsBulkDefault')));

const gradingRoutes = {
  path: 'grading',
  children: [
    {
      path: '',
      element: <GradingOverview />
    },
    {
      path: 'exams/:examId',
      element: <GradingExam />
    },
    {
      path: 'exams/:examId/sheets/:sheetId',
      element: <GradingSheet />
    },
    {
      path: 'exams/:examId/stimuli',
      element: <StimulusReview />
    },
    {
      path: 'review',
      element: <GradingReview />
    },
    {
      path: 'review/:examId',
      element: <ReviewCardScreen />
    },
    {
      path: 'review/:examId/near-silent',
      element: <NearSilentScreen />
    },
    {
      path: 'trust',
      element: <GradingTrust />
    },
    {
      path: 'settings',
      element: <GradingSettings />
    }
  ]
};

const MainRoutes = {
  path: '/',
  element: <MainLayout />,
  children: [
    {
      path: '/',
      element: <DashboardDefault />
    },
    {
      path: 'administrator',
      children: [
        {
          path: 'admins',
          element: <AdminsDefault />
        },
        {
          path: 'admins/create',
          element: <AdminsDefault />
        },
        {
          path: 'admins/:id/edit',
          element: <AdminsDefault />
        },
        {
          path: 'admins/delete',
          element: <AdminsDefault />
        },
        {
          path: 'academies',
          element: <AcademiesDefault />
        },
        {
          path: 'academies/create',
          element: <AcademiesDefault />
        },
        {
          path: 'academies/:id/edit',
          element: <AcademiesDefault />
        },
        {
          path: 'academies/delete',
          element: <AcademiesDefault />
        },
        {
          path: 'students-bulk',
          element: <StudentsBulkDefault />
        },
        {
          path: 'questions-bulk',
          element: <Typography>준비중</Typography>
        }
      ]
    },
    {
      path: 'users',
      children: [
        {
          path: 'teachers',
          element: <TeachersDefault />
        },
        {
          path: 'teachers/create',
          element: <TeachersDefault />
        },
        {
          path: 'teachers/:id/edit',
          element: <TeachersDefault />
        },
        {
          path: 'teachers/delete',
          element: <TeachersDefault />
        },
        {
          path: 'classes',
          element: <ClassesDefault />
        },
        {
          path: 'classes/create',
          element: <ClassesDefault />
        },
        {
          path: 'classes/:id/edit',
          element: <ClassesDefault />
        },
        {
          path: 'classes/delete',
          element: <ClassesDefault />
        },
        {
          path: 'students',
          element: <StudentsDefault />
        },
        {
          path: 'students/create',
          element: <StudentsDefault />
        },
        {
          path: 'students/:id/edit',
          element: <StudentsDefault />
        },
        {
          path: 'students/edit',
          element: <StudentsDefault />
        },
        {
          path: 'students/delete',
          element: <StudentsDefault />
        }
      ]
    },
    {
      path: 'exams',
      children: [
        {
          path: 'questions',
          element: <QuestionsDefault />
        },
        {
          path: 'questions/create',
          element: <QuestionsDefault />
        },
        {
          path: 'questions/:id/edit',
          element: <QuestionsDefault />
        },
        {
          path: 'questions/edit',
          element: <QuestionsDefault />
        },
        {
          path: 'questions/delete',
          element: <QuestionsDefault />
        },
        {
          path: 'questionsheets',
          element: <QuestionSheetsDefault />
        },
        {
          path: 'questionsheets/create',
          element: <QuestionSheetsDefault />
        },
        {
          path: 'questionsheets/:id/edit',
          element: <QuestionSheetsDefault />
        },
        {
          path: 'questionsheets/delete',
          element: <QuestionSheetsDefault />
        },
        {
          path: 'tests',
          element: <TestsDefault />
        },
        {
          path: 'tests/create',
          element: <TestsDefault />
        },
        {
          path: 'tests/:id/edit',
          element: <TestsDefault />
        },
        {
          path: 'tests/delete',
          element: <TestsDefault />
        },
        {
          path: 'evaluation',
          element: <EvaluationDefault />
        },
        {
          path: 'evaluation/create',
          element: <EvaluationDefault />
        },
        {
          path: 'evaluation/:id/edit',
          element: <EvaluationDefault />
        },
        {
          path: 'evaluation/delete',
          element: <EvaluationDefault />
        },
        {
          path: 'evaluation/:testId/answersheets',
          element: <EvaluationDefault />
        },
        {
          path: 'evaluation/:testId/answersheets/:answerSheetId/evaluate',
          element: <EvaluationDefault />
        }
      ]
    },
    ...(GRADING_ADMIN ? [gradingRoutes] : []),
    {
      path: '*',
      element: <Navigate to="/" replace />
    }
  ]
};

export default MainRoutes;
